import "./update.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import ModalBody from "reactstrap/lib/ModalBody";

import FormButton from "../../components/form/FormButton";
import ModalView from "../../components/modal/ModalView";
import PubSubTopics from "../../constants/pubSubTopics";
import { autoUpdatesOperations, autoUpdatesSelectors } from "../../modules/autoUpdates";
import { sendCustomError } from "../../modules/errorLogger";
import { registrationSelectors } from "../../modules/registration";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import versionNumberCompare from "../../utils/versionNumberCompare";

const renderCheckingMessage = () => {
  return (
    <Fragment>
      <div className="message">Checking for updates...</div>
    </Fragment>
  );
};
const renderAppStoreMessage = onCloseClick => {
  return (
    <Fragment>
      <div className="message">
        An update is available on the App Store. Please update Aeon Timeline to get the latest version.
      </div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="OK" primary={true} />
      </div>
    </Fragment>
  );
};

const renderDownloadingMessage = onCloseClick => {
  return (
    <Fragment>
      <div className="message">
        An update is available and downloading in the background. We will let you know when it is ready.
      </div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="OK" primary={true} />
      </div>
    </Fragment>
  );
};

const getExpiryMessage = expiryDate =>
  expiryDate
    ? `you are only eligible for app updates published on or before ${expiryDate.toDateString()}`
    : `you are not eligible to receive further updates`;

const renderOutOfDateMessage = (
  availableEditor,
  availableReader,
  expiryDate,
  onPurchaseClick,
  onInstallNowClick,
  onCloseClick
) => {
  let updateMessage = null;
  let updateButtonText = null;
  if (availableEditor) {
    updateMessage = `You can update to ${availableEditor} which was the last update available before that date`;
    updateButtonText = "Install";
  } else {
    updateMessage = `You can also install read only app updates for free`;
    updateButtonText = "Install read only update";
  }

  return (
    <Fragment>
      <div className="message">{`Version ${availableReader} is available, however ${getExpiryMessage(
        expiryDate
      )}.`}</div>
      <div className="buttons">
        <FormButton onClick={onPurchaseClick} label="Purchase updates" primary={true} />
      </div>
      <div className="message">{updateMessage}</div>
      <div className="buttons">
        <FormButton onClick={onInstallNowClick} label={updateButtonText} primary={true} />
      </div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="Close without updating" linkStyle={true} />
      </div>
    </Fragment>
  );
};

const renderOutOfDateNoUpdateMessage = (
  availableReader,
  expiryDate,
  onPurchaseClick,
  onInstallNowClick,
  onCloseClick
) => {
  return (
    <Fragment>
      <div className="message">{`Version ${availableReader} is available, however ${getExpiryMessage(
        expiryDate
      )}.`}</div>
      <div className="buttons">
        <FormButton onClick={onPurchaseClick} label="Purchase updates" primary={true} />
      </div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="Close without updating" linkStyle={true} />
      </div>
    </Fragment>
  );
};

const renderUpdateMessage = (availableVersion, onInstallNowClick, onCloseClick) => {
  return (
    <Fragment>
      <div className="message">{`Version ${availableVersion} is available and will be installed on next app launch.`}</div>
      <div className="message">Would you like to install it now?</div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="Not right now" />
        <FormButton onClick={onInstallNowClick} label="Install" primary={true} />
      </div>
    </Fragment>
  );
};

const renderUpToDateMessage = onCloseClick => {
  return (
    <Fragment>
      <div className="message">You are on the latest version</div>
      <div className="buttons">
        <FormButton onClick={onCloseClick} label="OK" primary={true} />
      </div>
    </Fragment>
  );
};

const getValidExpiryDate = expiryDate => {
  const expiryTs = Date.parse(expiryDate);
  return isNaN(expiryTs) ? null : new Date(expiryDate);
};

class UpdateModal extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onInstallNowClick = this.onInstallNowClick.bind(this);
    this.onPurchaseClick = this.onPurchaseClick.bind(this);
    this.sendExpiryErrorIfRequired = this.sendExpiryErrorIfRequired.bind(this);
  }

  componentDidMount() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.UPDATE_OPENED
    });
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.props.onCloseClick);
    this.sendExpiryErrorIfRequired();
  }

  componentDidUpdate(_prevProps) {
    this.sendExpiryErrorIfRequired();
  }

  componentWillUnmount() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.UPDATE_CLOSED
    });
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  onInstallNowClick() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.INSTALL_UPDATE_NOW
    });
  }

  onPurchaseClick() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.SHOW_REGISTRATION_MODAL
    });
  }

  sendExpiryErrorIfRequired() {
    const expiryDate = getValidExpiryDate(this.props.expiryDate);
    if (!expiryDate) {
      sendCustomError({
        type: "invalid_expiry_date",
        autoUpdatesData: this.props.autoUpdatesData
      });
    }
  }

  render() {
    const { autoUpdatesData, onCloseClick, isRegistered } = this.props;

    // The easiest way to test if a date is valid is to call parse to get a timestamp and check if that is a number
    const expiryDate = getValidExpiryDate(this.props.expiryDate);

    let modalHeading = (
      <div className="updateModalClose" onClick={onCloseClick}>
        <svg className="closeIcon mono">
          <use xlinkHref={"#interfaceClose"} />
        </svg>
      </div>
    );
    let modalFooter = null;
    let fixedHeight = null;

    let body;

    const isUpdateAvailable = autoUpdatesData.isEditingUpdateAvailable || autoUpdatesData.isReadonlyUpdateAvailable;
    const isOutOfDate = versionNumberCompare(autoUpdatesData.availableEditor, autoUpdatesData.availableReader) !== 0;

    if (autoUpdatesData.isChecking) {
      body = renderCheckingMessage();
    } else if (autoUpdatesData.updateRequiresShellUpdate && autoUpdatesData.useAppStore) {
      body = renderAppStoreMessage(onCloseClick);
    } else if (autoUpdatesData.isDownloading) {
      body = renderDownloadingMessage(onCloseClick);
    } else if (isRegistered && isUpdateAvailable && isOutOfDate) {
      body = renderOutOfDateMessage(
        autoUpdatesData.isEditingUpdateAvailable ? autoUpdatesData.availableEditor : null,
        autoUpdatesData.availableReader,
        expiryDate,
        this.onPurchaseClick,
        this.onInstallNowClick,
        onCloseClick
      );
    } else if (isUpdateAvailable) {
      const availableVersion = autoUpdatesData.isEditingUpdateAvailable
        ? autoUpdatesData.availableEditor
        : autoUpdatesData.availableReader;
      body = renderUpdateMessage(availableVersion, this.onInstallNowClick, onCloseClick);
    } else if (!isUpdateAvailable && isOutOfDate) {
      body = renderOutOfDateNoUpdateMessage(
        autoUpdatesData.availableReader,
        expiryDate,
        this.onPurchaseClick,
        this.onInstallNowClick,
        onCloseClick
      );
    } else {
      body = renderUpToDateMessage(onCloseClick);
    }

    return (
      <ModalView
        className="updateModal"
        header={modalHeading}
        footer={modalFooter}
        fixedHeight={fixedHeight}
        onBackdropClick={onCloseClick}
      >
        <ModalBody>{body}</ModalBody>
      </ModalView>
    );
  }
}

UpdateModal.propTypes = {
  autoUpdatesData: PropTypes.object,
  showDialog: PropTypes.bool.isRequired,
  isReadOnlyMode: PropTypes.bool,
  isRegistered: PropTypes.bool,
  expiryDate: PropTypes.string,
  onCloseClick: PropTypes.func
};

const mapStateToProps = state => {
  return {
    autoUpdatesData: autoUpdatesSelectors.getAutoUpdatesData(state.present),
    showDialog: autoUpdatesSelectors.showUpdateModal(state.present),
    isReadOnlyMode: registrationSelectors.getIsReadOnlyMode(state.present),
    isRegistered: registrationSelectors.getIsRegistered(state.present),
    expiryDate: registrationSelectors.getExpiryDate(state.present)
  };
};

const readonlyActions = {
  onCloseClick: autoUpdatesOperations.hideUpdateModal
};

const editingActions = {};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UpdateModal);
